import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/react-hooks';
import { type ApolloClient, type NormalizedCacheObject } from '@apollo/client';
import styled from 'styled-components';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { PAGE_TEXT } from './lang';
import {
  type SupervisorInfo,
  useGetSupervisorInvitations,
  useSendAcceptanceRequest,
} from '../../../graphql/ApplicationStatus/SupervisorInvitation';
import {
  Alert,
  type AlertDetails,
  Dialog,
  type DialogDetails,
  Loading,
} from '../../../../../components';

const {
  PAGE_DESCRIPTION,
  PAGE_GUIDE_1,
  PAGE_GUIDE_2,
  PAGE_REMINEDER_1,
  PAGE_REMINEDER_2,
  ACCEPT_THE_INVITATION,
  CONFIRM_INVITATION,
  CONFIRM_INVITATION_CONTENT,
  HAVE_ACCEPTED,
} = PAGE_TEXT;

export const ButtonContainer: React.FC<any> = ({ children }) => {
  const BtnCtnr = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    margin: 2rem 0;
    button {
      margin: 0 1rem;
    }
  `;
  return <BtnCtnr>{children}</BtnCtnr>;
};

export const HaveAccepted: React.FC<any> = props => {
  const { supervisors } = props;
  return (
    <div>
      {`${HAVE_ACCEPTED} from `}
      {supervisors
        .map((el: SupervisorInfo) => {
          return `${el.surname}, ${el.givenName} (${el.department})`;
        })
        .join(', ')}
    </div>
  );
};

const MAX_SELECT_SUPERVISOR = 1;

export const SupervisorInvitation: React.FC = () => {
  const theme = useTheme();
  const { applicationId } = useParams<{ applicationId: string }>();
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
  });
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  let acceptedSupervisors: SupervisorInfo[] = [];

  const { haveAccepted, supervisorInfo, loading, refetch, error } =
    useGetSupervisorInvitations(applicationId);

  if (!loading) acceptedSupervisors = supervisorInfo.filter(e => e.netId);

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: 'Error Occurs.' + (error as Error).message,
          alertType: 'error',
        }}
      />
    );
  }
  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const handleAcceptInvitation = (): void => {
    setDialogDetails({
      title: CONFIRM_INVITATION,
      content: CONFIRM_INVITATION_CONTENT,
      confirmAction: 'Yes',
      cancelAction: 'No',
    });
    setDialogOpen(true);
  };

  const handleConfirmAccept = async (): Promise<any> => {
    setDialogOpen(false);
    await useSendAcceptanceRequest(client, applicationId, checkedList[0])
      .then(data => {
        refetch();
      })
      .catch(err => {
        setAlertDetails({
          alertContent: `Error: ${err?.message}`,
          alertType: 'error',
        });
        setOpenAlert(true);
      });
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : haveAccepted ? (
        <>
          <HaveAccepted supervisors={acceptedSupervisors} />
        </>
      ) : (
        <>
          <span>{PAGE_DESCRIPTION}</span>
          <h5 style={{ fontWeight: 'bold', marginTop: '2rem' }}>
            {PAGE_GUIDE_1}
          </h5>
          <a
            className="underline my-4 text-sm"
            href="https://www.polyu.edu.hk/gs/prospective-students/find-a-supervisor/"
            target="_blank"
            rel="noreferrer"
          >
            {PAGE_GUIDE_2}
          </a>

          <Box width={'100%'} marginY={2}>
            <TableContainer component={Paper} sx={{ width: '60%' }}>
              <Table aria-label="simple table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell align="center">Name</TableCell>
                    <TableCell align="center">Email</TableCell>
                    <TableCell align="center">Department</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {supervisorInfo.map(row => (
                    <TableRow
                      key={row.netId}
                      sx={[
                        { '&:last-child td, &:last-child th': { border: 0 } },
                        checkedList.includes(row.netId)
                          ? { backgroundColor: '#E2C0C0' }
                          : {},
                      ]}
                    >
                      <TableCell component="th" scope="row">
                        <Checkbox
                          onChange={event => {
                            setCheckedList(currentState =>
                              event.target.checked
                                ? [
                                    ...currentState.filter(
                                      el => el !== row.netId,
                                    ),
                                    row.netId,
                                  ]
                                : currentState.filter(el => el !== row.netId),
                            );
                          }}
                          disabled={
                            checkedList.length > MAX_SELECT_SUPERVISOR - 1 &&
                            !checkedList.includes(row.netId)
                          }
                          checked={checkedList.includes(row.netId)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {row.surname}, {row.givenName}
                      </TableCell>
                      <TableCell align="center">{row.email}</TableCell>
                      <TableCell align="center">{row.department}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <div className="mt-4">
            <h2 style={{ color: theme.palette.primary.main }}>
              {PAGE_REMINEDER_1}
            </h2>
            <h2 style={{ color: theme.palette.primary.main }}>
              {PAGE_REMINEDER_2}
            </h2>
          </div>
          <ButtonContainer>
            <Button
              variant="contained"
              disabled={checkedList.length !== MAX_SELECT_SUPERVISOR}
              onClick={() => {
                handleAcceptInvitation();
              }}
            >
              {ACCEPT_THE_INVITATION}
            </Button>
          </ButtonContainer>
        </>
      )}

      <Dialog
        openDialog={dialogOpen}
        dialogTitle={dialogDetails.title}
        dialogAction={dialogDetails.confirmAction}
        cancelAction={dialogDetails.cancelAction}
        onDialogAction={async () => {
          await handleConfirmAccept();
        }}
        onDialogClose={() => {
          setDialogOpen(false);
        }}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {dialogDetails.content}
        </Typography>
      </Dialog>

      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </>
  );
};
